import {compact, noop} from 'lodash';
import React, {FC, useCallback, useEffect, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {AsyncHandlerOf, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {useLocalize} from '../../../../srcCommon/internationalization/internationalizationContext';
import {buildContactsManagerSyncsUrl} from '../../../helpers/contactsManagerHelpers';
import {
  AccountAttributeUpdateAction,
  accountAttributeUpdateReducer,
} from '../../../helpers/models/accountAttributeUpdateHelpers';
import {AccountModel} from '../../../models/api/accountModel';
import {CrmSyncIntegrationResourceTypesEnum, CrmSyncSourcesEnum} from '../../../models/api/crmSyncModel';
import {
  AccountDetailsSectionsEnum,
  AccountDetailsSectionsExpandState,
} from '../../../models/api/localPreferencesModel';
import {AccountDetailsCardsState} from '../../../store/panels/accounts/accountDetailsPanelState';
import {ContactsManagerParametersTypesEnum} from '../../../store/panels/contacts/contactsManager/parameters/contactsManagerParameters';
import {
  AccountDetailsPanelId,
  AppPanelId,
  ContactOrAccountDetailsPanelId,
} from '../../../store/panels/panelStateBase';
import {Localizer} from '../../../store/stateless/internationalizationSelectors';
import {Accordion} from '../../common/accordion/accordion';
import {CrmLastSyncedBannerContainer} from '../../common/crm/crmLastSyncedBannerContainer';
import {AsyncLoader} from '../../common/loaders/asyncLoader';
import {useLinkNavigation} from '../../common/navigation/useLinkNavigation';
import {HubspotCrmSyncDialogContainer} from '../crm/hubspot/hubspotCrmSyncDialogContainer';
import {SalesforceCrmSyncDialogContainer} from '../crm/salesforce/salesforceCrmSyncDialogContainer';
import {
  AccountDetailsTrackEventHandler,
  AccountDetailsTrackingEvent,
  buildAccountDetailsViewEvent,
} from '../tracking/accountDetailsTrackingHelpers';
import {AccountDetailsContext, AccountDetailsContextProps} from './accountDetailsContext';
import {AccountDetailsHeaderContainer} from './accountDetailsHeaderContainer';
import {AccountDetailsPluginHeaderContainer} from './accountDetailsPluginHeaderContainer';
import {
  buildAccountDetailsConversationSection,
  buildAccountInformationSection,
  buildContactsSection,
  OnAccountDetailsSectionToggledHandler,
} from './helpers/accordianSectionHelpers';
import {AccountDetailsHeaderSkeleton} from './loadingSkeletons/accountDetailsHeaderSkeleton';
import {AccountDetailsSectionSkeleton} from './loadingSkeletons/accountDetailsSectionSkeleton';

/*
 * Types.
 */

interface AccountDetailsProps {
  panelId: ContactOrAccountDetailsPanelId;
  accountId: number | undefined;
  account: AccountModel | undefined;
  sectionExpandState: AccountDetailsSectionsExpandState;
  status: AsyncStatusesEnum;
  popupAppPanelId?: AppPanelId;
  cardsState?: AccountDetailsCardsState;
  isEmbedded?: boolean;
  location?: ContactsManagerParametersTypesEnum;
  hasHubspotContactSync: boolean;
  hasCrmSyncFullPageSalesforce: boolean;

  // Conversations.
  conversationStatus: AsyncStatusesEnum | undefined;
  conversationIds: ReadonlyArray<number> | undefined;
  hasMoreConversations: boolean | undefined;

  onAccountDetailsSectionToggled: OnAccountDetailsSectionToggledHandler;
  addCardToAccount: AsyncHandlerOf<number>;
  fetchAccount: (panelId: ContactOrAccountDetailsPanelId, accountId: number) => void;
  onTrackEvent?: AccountDetailsTrackEventHandler;
}

/*
 * Styles.
 */

const StyledScrollableContentDiv = styled.div`
  overflow: auto;
`;

const StyledAccordion = styled(Accordion)`
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

const StyledErrorDiv = styled.div`
  padding: 64px 0;
`;

const StyledCrmLastSyncedBannerContainer = styled(CrmLastSyncedBannerContainer)`
  margin: 4px 4px 0 4px;
`;

/*
 * Component.
 */

export const AccountDetails: FC<AccountDetailsProps> = (props) => {
  const {
    panelId,
    accountId,
    account,
    status,
    sectionExpandState,
    popupAppPanelId,
    cardsState,
    isEmbedded,
    conversationStatus,
    conversationIds,
    hasMoreConversations,
    location,
    hasHubspotContactSync,
    hasCrmSyncFullPageSalesforce,
    onAccountDetailsSectionToggled,
    addCardToAccount,
    fetchAccount,
    onTrackEvent,
  } = props;
  const [crmSyncDialogToShow, setCrmSyncDialogToShow] = useState<CrmSyncSourcesEnum>();
  const localize = useLocalize();
  const {navigateToLink} = useLinkNavigation();

  const onTrackEventCallback = useCallback(
    (event: AccountDetailsTrackingEvent) => (onTrackEvent ? onTrackEvent(event) : noop),
    [onTrackEvent],
  );

  const accountDetailsContext = buildAccountDetailsContext(panelId, onTrackEventCallback);

  useEffect(() => {
    if (accountId) {
      onTrackEventCallback(buildAccountDetailsViewEvent(accountId, location));
    }
  }, [accountId, location, onTrackEventCallback]);

  // State and effect for handling adding new information to an account
  const [unsavedAccount, setUnsavedAccount] = useState<AccountModel | undefined>(account);
  const updateUnsavedAccount = (action: AccountAttributeUpdateAction) => {
    if (!unsavedAccount) {
      return;
    }
    const nextAccount = accountAttributeUpdateReducer(unsavedAccount, action);
    setUnsavedAccount(nextAccount);
  };
  useEffect(() => {
    if (account) {
      setUnsavedAccount(account);
    }
  }, [account]);

  const onManageSyncClick = (source: CrmSyncSourcesEnum) => {
    if (
      source === CrmSyncSourcesEnum.MICROSOFT_DYNAMICS ||
      (source === CrmSyncSourcesEnum.SALESFORCE && hasCrmSyncFullPageSalesforce) ||
      (source === CrmSyncSourcesEnum.HUBSPOT && hasHubspotContactSync)
    ) {
      navigateToLink(buildContactsManagerSyncsUrl(source));
    } else {
      setCrmSyncDialogToShow(source);
    }
  };

  // TODO: Add more fleshed out loading skeleton
  if (status === AsyncStatusesEnum.LOADING) {
    return (
      <AccountDetailsContext.Provider value={accountDetailsContext}>
        {maybeRenderPluginHeader(panelId, accountId, account, popupAppPanelId, isEmbedded)}
        <StyledScrollableContentDiv>
          <AccountDetailsHeaderSkeleton />
          <AccountDetailsSectionSkeleton
            section={AccountDetailsSectionsEnum.ACCOUNT_INFORMATION}
            shouldHideContentSkeleton={!sectionExpandState[AccountDetailsSectionsEnum.ACCOUNT_INFORMATION]}
          />
          <AccountDetailsSectionSkeleton
            section={AccountDetailsSectionsEnum.CONTACTS}
            shouldHideContentSkeleton={!sectionExpandState[AccountDetailsSectionsEnum.CONTACTS]}
            shouldRenderBottomBorder
          />
        </StyledScrollableContentDiv>
      </AccountDetailsContext.Provider>
    );
  }
  if (status === AsyncStatusesEnum.FAILED) {
    return (
      <AccountDetailsContext.Provider value={accountDetailsContext}>
        {maybeRenderPluginHeader(panelId, accountId, account, popupAppPanelId, isEmbedded)}
        <StyledErrorDiv>
          <AsyncLoader
            size={VisualSizesEnum.LARGE}
            status={AsyncStatusesEnum.FAILED}
            error={<Trans id="account-details-error-loading">Something went wrong.</Trans>}
            onRetryClick={() => {
              if (accountId) {
                fetchAccount(panelId, accountId);
              }
            }}
          />
        </StyledErrorDiv>
      </AccountDetailsContext.Provider>
    );
  }

  if (!account || !unsavedAccount) {
    return null;
  }

  return (
    <AccountDetailsContext.Provider value={accountDetailsContext}>
      {maybeRenderPluginHeader(panelId, accountId, account, popupAppPanelId, isEmbedded)}
      <StyledScrollableContentDiv>
        {maybeRenderCrmSyncBanner(account, onManageSyncClick)}
        <AccountDetailsHeaderContainer account={account} />
        <StyledAccordion
          key="standard-accordion"
          allowMultiSelect
          sections={buildAccordionSections(
            panelId,
            sectionExpandState,
            cardsState,
            account,
            conversationStatus,
            conversationIds,
            hasMoreConversations,
            onAccountDetailsSectionToggled,
            localize,
            addCardToAccount,
            unsavedAccount,
            updateUnsavedAccount,
            onTrackEventCallback,
          )}
        />
      </StyledScrollableContentDiv>
      {maybeRenderCrmSyncDialog(panelId, crmSyncDialogToShow, () => setCrmSyncDialogToShow(undefined))}
    </AccountDetailsContext.Provider>
  );
};

/*
 * Helpers.
 */

function maybeRenderCrmSyncDialog(
  panelId: ContactOrAccountDetailsPanelId,
  crmSyncDialogToShow: CrmSyncSourcesEnum | undefined,
  onRequestClose: Handler,
) {
  switch (crmSyncDialogToShow) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return <SalesforceCrmSyncDialogContainer panelId={panelId} onRequestClose={onRequestClose} />;
    case CrmSyncSourcesEnum.HUBSPOT:
      return <HubspotCrmSyncDialogContainer panelId={panelId} onRequestClose={onRequestClose} />;
    default:
      return null;
  }
}

function maybeRenderCrmSyncBanner(
  account: AccountModel | undefined,
  onManageSyncClick: HandlerOf<CrmSyncSourcesEnum>,
) {
  if (!account || !account.externalInfo) {
    return null;
  }

  switch (account.externalInfo.source) {
    case CrmSyncSourcesEnum.SALESFORCE:
      return (
        <StyledCrmLastSyncedBannerContainer
          resourceType={CrmSyncIntegrationResourceTypesEnum.ACCOUNT}
          source={account.externalInfo.source}
          onManageSyncClick={() => onManageSyncClick(CrmSyncSourcesEnum.SALESFORCE)}
          shouldRenderTooltip
        />
      );
    case CrmSyncSourcesEnum.HUBSPOT:
      return (
        <StyledCrmLastSyncedBannerContainer
          resourceType={CrmSyncIntegrationResourceTypesEnum.ACCOUNT}
          source={account.externalInfo.source}
          onManageSyncClick={() => onManageSyncClick(CrmSyncSourcesEnum.HUBSPOT)}
          shouldRenderTooltip
        />
      );
    case CrmSyncSourcesEnum.MICROSOFT_DYNAMICS:
      return (
        <StyledCrmLastSyncedBannerContainer
          resourceType={CrmSyncIntegrationResourceTypesEnum.ACCOUNT}
          source={account.externalInfo.source}
          onManageSyncClick={() => onManageSyncClick(CrmSyncSourcesEnum.MICROSOFT_DYNAMICS)}
          shouldRenderTooltip
        />
      );
    default:
      return null;
  }
}

const maybeRenderPluginHeader = (
  panelId: ContactOrAccountDetailsPanelId,
  accountId: number | undefined,
  account: AccountModel | undefined,
  popupAppPanelId: AppPanelId | undefined,
  isEmbedded?: boolean,
) => {
  if (isEmbedded) {
    return null;
  }
  // We can cast panelId as AccountDetailsPanelId since the plugin header is only shown for account details
  return (
    <AccountDetailsPluginHeaderContainer
      panelId={panelId as AccountDetailsPanelId}
      accountId={accountId}
      account={account}
      isInPopup={Boolean(popupAppPanelId)}
    />
  );
};

const buildAccordionSections = (
  panelId: ContactOrAccountDetailsPanelId,
  expandState: AccountDetailsSectionsExpandState,
  cardsState: AccountDetailsCardsState | undefined,
  account: AccountModel,
  conversationStatus: AsyncStatusesEnum | undefined,
  conversationIds: ReadonlyArray<number> | undefined,
  hasMoreConversations: boolean | undefined,
  onAccountDetailsSectionToggled: OnAccountDetailsSectionToggledHandler,
  localize: Localizer,
  addCardToAccount: AsyncHandlerOf<number>,
  unsavedAccount: AccountModel,
  updateUnsavedAccount: HandlerOf<AccountAttributeUpdateAction>,
  onTrackEvent: AccountDetailsTrackEventHandler,
) =>
  compact([
    buildAccountInformationSection({
      panelId,
      expandState,
      onAccountDetailsSectionToggled,
      unsavedAccount,
      updateUnsavedAccount,
    }),
    buildAccountDetailsConversationSection({
      panelId,
      expandState,
      conversationStatus,
      conversationIds,
      hasMoreConversations,
      accountId: account.id,
      onAccountDetailsSectionToggled,
    }),
    buildContactsSection({
      panelId,
      expandState,
      cardsState,
      account,
      onAccountDetailsSectionToggled,
      localize,
      addCardToAccount,
      onTrackEvent,
    }),
  ]);

function buildAccountDetailsContext(
  panelId: ContactOrAccountDetailsPanelId,
  onTrackEvent?: AccountDetailsTrackEventHandler,
): AccountDetailsContextProps {
  return {
    panelId,
    onTrackEvent: onTrackEvent ?? noop,
  };
}
